/**
 * GOAT: paints the cart's "working" state in the same frame as the click.
 *
 * `component-cart-items` puts its quantity handler behind a 300 ms trailing debounce, and the
 * shimmer it shows lives INSIDE that debounced handler — so a deliberate single click on + or -
 * produced no feedback at all for 300 ms before the request was even sent. Combined with the
 * `.cart-items-disabled` guard that runs for the whole flight, that is the bulk of why cart
 * quantity changes "feel slow".
 *
 * This listener runs in the capture phase, ahead of the debounce, and sets exactly the attribute
 * `TextComponent.shimmer()` sets (`assets/utilities.js`) on exactly the nodes the debounced
 * handler would have shimmered. The request path, the debounce and the coalescing of a rapid click
 * burst are all untouched; `resetShimmer()` on the response clears these the same way.
 */
import { ThemeEvents } from '@theme/events';

const SHIMMER_ATTRIBUTE = 'shimmer';

document.addEventListener(
  ThemeEvents.quantitySelectorUpdate,
  (event) => {
    const detail = /** @type {{ quantity?: number, cartLine?: number }} */ (
      /** @type {CustomEvent} */ (event).detail
    );

    // Cart lines only: the PDP's own quantity selector has no `cartLine` and shimmers nothing.
    if (!detail?.cartLine) return;

    // A removal (quantity 0) animates the row out instead of shimmering it — leave it alone.
    if (!(detail.quantity > 0)) return;

    const target = event.target;
    if (!(target instanceof Element)) return;

    const host = target.closest('cart-items-component');
    if (!host) return;

    const row = target.closest('[ref="cartItemRows[]"]');
    row?.querySelector('text-component')?.setAttribute(SHIMMER_ATTRIBUTE, '');
    host.querySelector('[ref="cartTotal"]')?.setAttribute(SHIMMER_ATTRIBUTE, '');
  },
  true
);
